import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Collapse,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AnalysisPage = () => {
  const navigate = useNavigate();
  const [isGuideExpanded, setIsGuideExpanded] = useState(false);

  // 待审核的保单数据
  const policies = [
    {
      id: 1,
      policyNumber: 'POL-2024-001',
      type: 'Auto Insurance',
      client: 'John Smith',
      submittedDate: '2024-01-25',
      complexity: 'medium',
      status: 'under_review',
    },
    {
      id: 2,
      policyNumber: 'POL-2024-002',
      type: 'Home Insurance',
      client: 'Emma Wilson',
      submittedDate: '2024-01-24',
      complexity: 'low',
      status: 'requires_info',
    }
  ];

  // 已批准的保单数据
  const approvedPolicies = [
    {
      id: 3,
      policyNumber: 'POL-2024-003',
      type: 'Life Insurance',
      client: 'Michael Brown',
      submittedDate: '2024-01-23',
      approvedDate: '2024-01-24',
      complexity: 'high',
      status: 'approved',
    }
  ];

  // 复杂度等级配置
  const complexityLevels = {
    low: {
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      label: 'Simple',
      description: '• No accidents in past 6 years\n• No traffic tickets\n• No out-of-province driving experience'
    },
    medium: {
      icon: <WarningIcon fontSize="small" />,
      color: 'warning',
      label: 'Moderate',
      description: '• Has accidents or traffic tickets\n• No out-of-province driving experience\n• Standard documentation'
    },
    high: {
      icon: <ErrorIcon fontSize="small" />,
      color: 'error',
      label: 'Complex',
      description: '• Multiple accidents or tickets\n• Out-of-province driving experience\n• Letter of Experience (LOE) required\n• Complex documentation review needed'
    }
  };

  // 获取复杂度标签
  const getComplexityChip = (level) => {
    const config = complexityLevels[level];
    return (
      <Tooltip 
        title={
          <Box sx={{ whiteSpace: 'pre-line', p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {config.label} Case Criteria:
            </Typography>
            <Typography variant="body2">
              {config.description}
            </Typography>
          </Box>
        }
        arrow
        placement="top"
      >
        <Chip 
          icon={config.icon}
          label={config.label}
          color={config.color}
          size="small"
          variant="outlined"
        />
      </Tooltip>
    );
  };

  // 获取状态标签
  const getStatusChip = (status) => {
    const statusConfig = {
      under_review: { label: 'Under Review', color: 'warning' },
      approved: { label: 'Approved', color: 'success' },
      requires_info: { label: 'Requires Info', color: 'error' }
    };

    const config = statusConfig[status];
    return (
      <Chip 
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Home
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Submission Status 
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Track real-time status updates for insurance submissions
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { title: 'Under Review', count: 8, color: '#ff9800' },
          { title: 'Approved', count: 15, color: '#4caf50' },
          { title: 'Requires Info', count: 3, color: '#f44336' },
          { title: 'Total Policies', count: 26, color: '#1976d2' }
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: 'white'
              }}
            >
              <Typography variant="h4" sx={{ color: stat.color, fontWeight: 600, mb: 1 }}>
                {stat.count}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {stat.title}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* 复杂度说明卡片 - 修改为可折叠 */}
      <Paper elevation={0} sx={{ 
        p: 2, 
        mb: 4, 
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#f8f9fa'
        }
      }}>
        <Box 
          onClick={() => setIsGuideExpanded(!isGuideExpanded)}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <IconButton size="small" sx={{ mr: 1 }}>
            <ExpandMoreIcon sx={{ 
              transform: isGuideExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s'
            }}/>
          </IconButton>
          <Typography variant="h6" sx={{ color: '#1976d2' }}>
            Case Complexity Classification
          </Typography>
        </Box>
        
        <Collapse in={isGuideExpanded}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Simple Cases */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2,
                borderLeft: '4px solid #4caf50',
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label="Simple" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ mr: 1.5 }}
                  />
                  <Typography variant="subtitle2">Low Risk Profile</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  • No claims in 6+ years
                  <br />
                  • Clean driving record
                  <br />
                  • Local jurisdiction
                </Typography>
              </Box>
            </Grid>

            {/* Moderate Cases */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2,
                borderLeft: '4px solid #ffb74d',
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: 1 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label="Moderate" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ mr: 1.5 }}
                  />
                  <Typography variant="subtitle2">Standard Risk</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  • Minor infractions
                  <br />
                  • Single claim history
                  <br />
                  • Standard documents
                </Typography>
              </Box>
            </Grid>

            {/* Complex Cases */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2,
                borderLeft: '4px solid #f44336',
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label="Complex" 
                    size="small" 
                    color="error" 
                    variant="outlined"
                    sx={{ mr: 1.5 }}
                  />
                  <Typography variant="subtitle2">High Complexity</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  • Multiple claims
                  <br />
                  • Cross-border exposure
                  <br />
                  • LOE required
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Pending Review Policies Table */}
      <Typography variant="h6" sx={{ mb: 3, color: '#1976d2' }}>
        Pending Review Policies
      </Typography>
      <TableContainer component={Paper} elevation={2} sx={{ mb: 6 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Policy Number</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Submitted Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Case Complexity</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.map((policy) => (
              <TableRow 
                key={policy.id}
                onClick={() => navigate(`/analysis/policy-review/${policy.id}`)}
                sx={{ 
                  '&:hover': { 
                    bgcolor: '#f8f9fa',
                    cursor: 'pointer' 
                  },
                  transition: 'background-color 0.2s'
                }}
              >
                <TableCell>{policy.policyNumber}</TableCell>
                <TableCell>{policy.type}</TableCell>
                <TableCell>{policy.client}</TableCell>
                <TableCell>{policy.submittedDate}</TableCell>
                <TableCell>{getComplexityChip(policy.complexity)}</TableCell>
                <TableCell>{getStatusChip(policy.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Approved Policies Table */}
      <Typography variant="h6" sx={{ mb: 3, color: '#1976d2' }}>
        Approved Policies
      </Typography>
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Policy Number</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Approved Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Case Complexity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {approvedPolicies.map((policy) => (
              <TableRow 
                key={policy.id}
                onClick={() => navigate(`/analysis/approved-policy/${policy.id}`)}
                sx={{ 
                  '&:hover': { 
                    bgcolor: '#f8f9fa',
                    cursor: 'pointer' 
                  },
                  transition: 'background-color 0.2s'
                }}
              >
                <TableCell>{policy.policyNumber}</TableCell>
                <TableCell>{policy.type}</TableCell>
                <TableCell>{policy.client}</TableCell>
                <TableCell>{policy.approvedDate}</TableCell>
                <TableCell>{getComplexityChip(policy.complexity)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AnalysisPage; 